<script setup name="TvbHomeColumnsNews">
import request from '@/utils/request'
import { reactive, inject } from 'vue'
import { action } from './../../../../utils/gtm'
import { toReativeTime } from './../../../../utils/index'

const size = inject('size')
const props = defineProps({
  info: {
    type: Object,
    default: null
  },
  linkTarget: {
    type: Number,
    default: 1
  }
})
const news = reactive({
  list: [],
  moreUrl: ''
})

if (props.info.api) {
  request({
    url: props.info.api,
    method: 'get'
  }).then(({ data: { list, more_url } }) => {
    news.list = list
    news.moreUrl = more_url
  })
}

const title = (item) => item.title || item.post_content
// item?.post_tags?.[0] ||
const tag = (item) => item.category_title || item.category_name
const datetime = (item) => item?.publish_datetime || item?.post_datetime || item?.publish_at
const tracking = (item) => {
  action({
    page_title: props.info.title,
    screen_class: title(item),
    screenID: 'S001',
    eventCategory: 'home_banner',
    eventLabel: props.info.api,
    eventID: item.id || item.post_id || 'B001',
    screenLabel: item.jump_url || item.link
  })
}
</script>

<template>
  <section class="tvb-home-columns-news" :class="[size]">
    <header class="tvb-home-columns-news-header">
      <h2 class="tvb-home-columns-news-title">
        <component
          :is="news?.moreUrl ? 'tvb-router-link' : 'span'"
          :link-target="linkTarget"
          :to="news?.moreUrl"
        >
          {{ info.title }}
        </component>
      </h2>
      <!-- && news.list.length > ['s', 'xs'].includes(size) ? 5 : 9 -->
      <tvb-button
        v-if="news.moreUrl"
        :style="{ '--tvb-button-height': ['s', 'xs'].includes(size) ? '24px' : '36px' }"
        class="tvb-home-columns-news_more"
        :type="!['s', 'xs'].includes(size) ? 'more' : 'more-text'"
        :animation="!['s', 'xs'].includes(size) ? 'more' : ''"
        icon="icon_list_arrow_grey"
        :path="news.moreUrl"
        :link-target="linkTarget"
        >查看更多</tvb-button
      >
    </header>
    <main class="tvb-home-columns-news-main">
      <div class="tvb-home-columns-news-main-four">
        <TvbRouterLink
          v-for="(item, index) in news.list.slice(0, ['s', 'xs'].includes(size) ? 1 : 4)"
          :key="index"
          :to="item.jump_url || item.link"
          class="tvb-home-columns-news-main-card tvb-hairline--surround"
          @click="tracking(item)"
          :link-target="item.link_target || linkTarget"
        >
          <TvbImage
            :src="item.thumbnails || item.image_info"
            class="tvb-home-columns-news-main-card-image"
            :is-video="!!item.video_url"
            :play-icon="{ size: { xl: 48, l: 48, m: 48, s: 40, xs: 40 } }"
            backgroundc="#f0f1f2"
            :border-radius="['s', 'xs'].includes(size) ? 6 : 8"
            transform
            :alt="title(item)"
          />
          <div class="tvb-home-columns-news-main-card-info">
            <h3 class="tvb-home-columns-news-main-title" :title="title(item)">
              <span>{{ title(item) }}</span>
            </h3>
            <p class="tvb-home-columns-news-main-tag">
              <span v-if="tag(item)" :title="tag(item)">{{ tag(item) }}</span>
              <time v-if="datetime(item)" :datetime="datetime(item) * 1000">
                {{ toReativeTime(datetime(item)) }}
              </time>
            </p>
          </div>
        </TvbRouterLink>
      </div>
      <div class="tvb-home-columns-news-main-five">
        <TvbRouterLink
          v-for="(item, index) in news.list.slice(
            ['s', 'xs'].includes(size) ? 1 : 4,
            ['s', 'xs'].includes(size) ? 5 : 9
          )"
          :key="index"
          :to="item.jump_url || item.link"
          :link-target="item.link_target || linkTarget"
          class="tvb-home-columns-news-main-card"
          @click="tracking(item)"
        >
          <TvbImage
            :src="item.thumbnails || item.image_info"
            class="tvb-home-columns-news-main-card-image"
            :is-video="!!item.video_url"
            transform
            border
            :play-icon="{ size: { xl: 48, l: 48, m: 48, s: 32, xs: 32 } }"
            :key="item.id || index"
            :border-radius="['s', 'xs'].includes(size) ? 6 : 8"
            :alt="title(item)"
          />
          <div class="tvb-home-columns-news-main-card-info">
            <h3 class="tvb-home-columns-news-main-card-title" :title="title(item)">
              <span>{{ title(item) }}</span>
            </h3>
            <p class="tvb-home-columns-news-main-tag">
              <span v-if="tag(item)" :title="tag(item)">{{ tag(item) }}</span>
              <time v-if="datetime(item)" :datetime="datetime(item) * 1000">
                {{ toReativeTime(datetime(item)) }}
              </time>
            </p>
          </div>
        </TvbRouterLink>
      </div>
    </main>
    <footer
      class="tvb-home-columns-news-footer"
      v-if="['s', 'xs'].includes(size) && news.list.length > 5 && news.moreUrl"
    >
      <TvbRouterLink
        class="tvb-home-columns-news-footer-more"
        :to="news.moreUrl"
        :link-target="linkTarget"
      >
        查看更多 <TvbSvgIcon class="arrow-icon" name="icon_list_arrow_grey" />
      </TvbRouterLink>
    </footer>
  </section>
</template>

<style lang="scss" scoped>
.tvb-home-columns-news {
  min-width: 320px;
  .tvb-home-columns-news-header {
    display: flex;
    justify-content: space-between;
    .tvb-home-columns-news-title {
      font-size: 24px;
      font-weight: 500;
      color: #1e1e1e;
      line-height: 36px;
      letter-spacing: 0.5px;
      > a {
        text-decoration: none;
        color: inherit;
        transition: color 0.15s linear;
        &:hover {
          color: rgba(0, 98, 163, 1);
        }
      }
    }
    :deep(.tvb-home-columns-news_more) {
      &.btn-more .svg-icon {
        margin-left: 1px;
      }
    }
  }

  .tvb-home-columns-news-main {
    display: flex;
    margin-top: 16px;
    .tvb-home-columns-news-main-four {
      width: calc((100% - 16px * 2) / 3 * 2 + 16.02px);
      margin-right: 8px;
      display: flex;
      flex-wrap: wrap;

      .tvb-home-columns-news-main-card {
        display: block;
        width: calc((100% - 16px) / 2);
        text-decoration: none;
        background: #f8f9fa;
        border-radius: 8px;
        overflow: hidden;
        --tvb-hairline-border-radius: calc(2 * 8px);

        .tvb-home-columns-news-main-card-image {
          width: 100%;
          height: auto;
          aspect-ratio: 1/0.5625;
          overflow: hidden;
          border-radius: 8px;
        }

        .tvb-home-columns-news-main-card-info {
          padding: 10px 16px 12px;

          .tvb-home-columns-news-main-title {
            font-size: 20px;
            font-weight: 500;
            color: #333333;
            line-height: 26px;
            height: 52px;
            letter-spacing: 0.5px;

            @include line-text-ellipsis(2);
            span {
              transition: color 0.15s linear;
              &:hover {
                color: rgba(0, 98, 163, 1);
              }
            }
          }

          .tvb-home-columns-news-main-tag {
            margin-top: 4px;
            font-size: 16px;
            font-weight: 400;
            color: #818d95;
            line-height: 22px;
            display: flex;
            align-items: center;
            letter-spacing: 0.5px;
            white-space: nowrap;

            span {
              display: inline-block;
              margin-right: 12px;
              white-space: nowrap;
              max-width: 60%;
              overflow: hidden;
              text-overflow: ellipsis;
            }

            time {
              white-space: nowrap;
            }
          }
        }

        &:nth-child(1) {
          margin-right: 8px;
          margin-bottom: 8px;
        }
        &:nth-child(2) {
          margin-left: 8px;
          margin-bottom: 8px;
        }
        &:nth-child(3) {
          margin-right: 8px;
          margin-top: 8px;
        }
        &:nth-child(4) {
          margin-left: 8px;
          margin-top: 8px;
        }
      }
    }
    .tvb-home-columns-news-main-five {
      width: calc((100% - 16px * 2) / 3 * 1);
      margin-left: 8px;
      .tvb-home-columns-news-main-card {
        width: 100%;
        display: flex;
        text-decoration: none;
        margin-top: 16px;
        overflow: hidden;
        &:first-child {
          margin-top: 0;
        }
        .tvb-home-columns-news-main-card-image {
          width: calc((100% - 8px) / 2 + 0.02px);
          margin-right: 6px;
          // height: auto;
          aspect-ratio: 1/0.5652173913043478;
          border-radius: 8px;
          overflow: hidden;
        }

        .tvb-home-columns-news-main-card-info {
          width: calc((100% - 8px) / 2);
          // margin-left: 2px;
          padding-left: 5px;
          .tvb-home-columns-news-main-card-title {
            font-size: 20px;
            font-weight: 500;
            color: #333333;
            line-height: 26px;
            height: 52px;
            overflow: hidden;
            letter-spacing: 0.5px;

            @include line-text-ellipsis(2);
            span {
              transition: color 0.15s linear;
              &:hover {
                color: rgba(0, 98, 163, 1);
              }
            }
          }

          .tvb-home-columns-news-main-tag {
            margin-top: 4px;
            font-size: 16px;
            font-weight: 400;
            color: #818d95;
            line-height: 22px;
            white-space: nowrap;
            display: flex;
            align-items: center;
            letter-spacing: 0.5px;
            span {
              margin-right: 12px;
              white-space: nowrap;
              white-space: nowrap;
              display: inline-block;
              max-width: 50%;
              overflow: hidden;
              text-overflow: ellipsis;
            }
          }
        }
      }
    }
  }

  .tvb-home-columns-news-footer {
    display: none;
  }

  &.s {
    .tvb-home-columns-news-main {
      padding: 0px 16px !important;
    }
    .tvb-home-columns-news-footer {
      padding: 16px 16px 0 !important;
    }
  }
  &.s,
  &.xs {
    .tvb-home-columns-news-header {
      padding: 0 12px;
      align-items: center;
      .tvb-home-columns-news-title {
        font-size: 17px;
        font-weight: 500;
        color: #1e1e1e;
        line-height: 24px;
      }
    }
    .tvb-home-columns-news-main {
      flex-direction: column;
      padding: 0px 12px;
      margin-top: 10px;
      .tvb-home-columns-news-main-four {
        width: 100%;
        .tvb-home-columns-news-main-card {
          width: 100%;
          margin: 0;
          border-radius: 6px;

          --tvb-hairline-border-radius: calc(2 * 6px);

          .tvb-home-columns-news-main-card-image {
            aspect-ratio: 1 / 0.5628415300546448;
            border-radius: 6px;

            @supports not (aspect-ratio: 1 / 0.5628415300546448) {
              height: 196px;
            }
          }

          .tvb-home-columns-news-main-card-info {
            padding: 8px 10px 10px;
            .tvb-home-columns-news-main-title {
              font-size: 16px;
              font-weight: 400;
              color: #333333;
              line-height: 22px;
              height: 22px;
              overflow: hidden;

              @include line-text-ellipsis(2);
            }

            .tvb-home-columns-news-main-tag {
              font-size: 13px;
              font-weight: 400;
              color: #818d95;
              line-height: 18px;
              letter-spacing: 0;
            }
          }
        }
      }
      .tvb-home-columns-news-main-five {
        width: 100%;
        margin: 0;
        .tvb-home-columns-news-main-card {
          width: 100%;
          margin: 0;
          margin-top: 16px;

          .tvb-home-columns-news-main-card-image {
            width: auto;
            height: 72px;
            aspect-ratio: 1 / 0.5625;
            border-radius: 6px;
            margin-right: 5px;
            @supports not (aspect-ratio: 1 / 0.5625) {
              width: 127px;
            }
          }

          .tvb-home-columns-news-main-card-info {
            padding-left: 5px;
            width: calc(100vw - 32.82051282051282vw - 5px - 12px * 2);
            .tvb-home-columns-news-main-card-title {
              font-size: 16px;
              font-weight: 400;
              color: #333333;
              line-height: 22px;
              height: 44px;
            }

            .tvb-home-columns-news-main-tag {
              font-size: 13px;
              font-weight: 400;
              color: #818d95;
              line-height: 18px;
              margin-top: 4px;
              letter-spacing: 0;
            }
          }
        }
      }
    }
    .tvb-home-columns-news-footer {
      display: block;
      padding: 16px 12px 0;

      .tvb-home-columns-news-footer-more {
        width: 100%;
        height: 40px;
        background: #f8f9fa;
        border-radius: 5px;
        border: 0.5px solid rgba(163, 178, 188, 0.35);
        box-sizing: border-box;
        display: block;
        text-align: center;
        line-height: 40px;
        font-size: 14px;
        font-weight: 400;
        color: #555555;
        letter-spacing: 0.3px;
        display: flex;
        align-items: center;
        justify-content: center;
        > svg {
          width: 12px;
          height: 12px;
          transform: rotate(180deg);
        }

        &:active {
          opacity: 0.75;
        }
      }
    }
  }
}
</style>
